import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getOptimizerRuns,
  getOptimizerRunDetail,
  getOptimizerArtifacts,
  OptimizerRunSummary,
  OptimizerRunDetail,
  OptimizerArtifactSamples,
} from '../../services/providerReviewService';

/** Rough blended cost for the reviewer's intuition (概算; not billing data). */
const APPROX_USD_PER_MTOKEN = 1.0;

/**
 * Per-bot optimization run history + expandable decision-log detail
 * (design §9). Rendered inside BotDetailPanel so the reviewer sees WHAT the
 * optimizer did and WHY (decisions with rationales), the self-evaluation
 * Q/A grades, and the tool-config diff — the evidence behind 承認 / 差し戻し.
 */
const GRADE_CLASS: Record<string, string> = {
  good: 'opt-grade-good', partial: 'opt-grade-partial', poor: 'opt-grade-poor', error: 'opt-grade-error',
};

const EvalBadges: React.FC<{ s?: { good: number; partial: number; poor: number; errors: number } }> = ({ s }) => {
  if (!s) return null;
  return (
    <span className="opt-eval-badges">
      <span className="opt-grade-good">✓{s.good}</span>
      <span className="opt-grade-partial">△{s.partial}</span>
      <span className="opt-grade-poor">✗{s.poor}</span>
      {s.errors > 0 && <span className="opt-grade-error">!{s.errors}</span>}
    </span>
  );
};

const OptimizerRunsSection: React.FC<{ botId: string }> = ({ botId }) => {
  const { t } = useTranslation();
  const [runs, setRuns] = useState<OptimizerRunSummary[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [detail, setDetail] = useState<OptimizerRunDetail | null>(null);
  const [samples, setSamples] = useState<OptimizerArtifactSamples[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRuns(await getOptimizerRuns(botId));
    } catch (e) {
      // Optimizer not wired in this env → hide the section quietly.
      setRuns([]);
    }
  }, [botId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (runTs: string) => {
    if (open === runTs) { setOpen(null); setDetail(null); return; }
    setOpen(runTs);
    setDetail(null);
    setSamples(null);
    setError(null);
    try {
      setDetail(await getOptimizerRunDetail(botId, runTs));
      getOptimizerArtifacts(botId, runTs).then(setSamples).catch(() => setSamples([]));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  if (runs.length === 0) return null;

  const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');
  const toolsOf = (cfg?: Record<string, unknown>) =>
    Array.isArray(cfg?.tools) ? (cfg!.tools as string[]).join(', ') : '—';

  return (
    <section className="detail-block">
      <h4>{t('review.optimizer.title', 'Optimization runs')} ({runs.length})</h4>
      <ul className="opt-run-list">
        {runs.map((r) => (
          <li key={r.runTs} className="opt-run-item">
            <button className="opt-run-row" onClick={() => toggle(r.runTs)}>
              <span className={`opt-status opt-status-${r.status}`}>
                {t(`review.optimizer.status.${r.status}`, r.status)}
              </span>
              <span className="opt-trigger">{t(`review.optimizer.trigger.${r.trigger}`, r.trigger)}</span>
              <EvalBadges s={r.evalSummary} />
              <span className="detail-muted">{fmt(r.createdAt)}</span>
              <span className="opt-caret">{open === r.runTs ? '▾' : '▸'}</span>
            </button>
            {open === r.runTs && (
              <div className="opt-run-detail">
                {error && <div className="review-error">{error}</div>}
                {!detail && !error && <p className="detail-muted">…</p>}
                {detail && (
                  <>
                    {detail.inputNote && (
                      <p className="opt-note">
                        <b>{t('review.optimizer.inputNote', 'Provider note driving this run')}:</b> {detail.inputNote}
                      </p>
                    )}
                    {detail.error && <div className="review-error">{detail.error}</div>}

                    <h5>{t('review.optimizer.decisions', 'Decisions')}</h5>
                    <ol className="opt-decisions">
                      {detail.decisions.map((d, i) => (
                        <li key={i}>
                          <span className="opt-step">{d.step}</span> {d.decision}
                          <div className="detail-muted opt-rationale">{d.rationale}</div>
                        </li>
                      ))}
                    </ol>

                    {detail.configDiff && (
                      <>
                        <h5>{t('review.optimizer.configDiff', 'Tool wiring')}</h5>
                        <p className="opt-diff">
                          <span className="detail-muted">{toolsOf(detail.configDiff.before)}</span>
                          {' → '}
                          <b>{toolsOf(detail.configDiff.after)}</b>
                        </p>
                      </>
                    )}

                    {detail.evalResults.length > 0 && (
                      <>
                        <h5>
                          {t('review.optimizer.eval', 'Self-evaluation')} <EvalBadges s={detail.evalSummary} />
                        </h5>
                        <ul className="opt-eval-list">
                          {detail.evalResults.map((e, i) => (
                            <li key={i} className="opt-eval-item">
                              <div>
                                <span className={GRADE_CLASS[e.grade] || ''}>
                                  {t(`review.optimizer.grade.${e.grade}`, e.grade)}
                                </span>{' '}
                                <b>{e.question}</b>
                                {e.source === 'readjust_note' && (
                                  <span className="chip">{t('review.optimizer.fromNote', 'from provider note')}</span>
                                )}
                              </div>
                              <div className="opt-eval-answer">{e.answer || '—'}</div>
                              <div className="detail-muted">{e.judgeNotes}</div>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    {detail.inventory.length > 0 && (
                      <>
                        <h5>{t('review.optimizer.inventory', 'Knowledge files')}</h5>
                        <ul className="opt-inventory">
                          {detail.inventory.map((f) => (
                            <li key={f.fileId}>
                              {f.fileName} <span className="detail-muted">
                                {typeof f.size === 'number' ? `${Math.ceil(f.size / 1000)}KB · ` : ''}
                                {f.s3Key
                                  ? t('review.optimizer.readable', 'restructurable')
                                  : t('review.optimizer.legacy', 'file_search only (pre-optimizer upload)')}
                              </span>
                              {f.description && (
                                <div className="detail-muted file-desc">
                                  {t('review.optimizer.providerDesc', 'Provider note')}: {f.description}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    {samples && samples.length > 0 && (
                      <>
                        <h5>{t('review.optimizer.samples', 'Extracted samples')}</h5>
                        {samples.map((a) => (
                          <div key={a.kind} className="opt-samples">
                            <div className="detail-muted">{a.kind} — {a.entries} {t('review.optimizer.entries', 'entries')}</div>
                            <ul>
                              {a.samples.map((entry, i) => (
                                <li key={i} className="opt-sample-entry">
                                  {String((entry as any).terminology ?? (entry as any).question ?? (entry as any).title ?? '')}
                                  <span className="detail-muted"> — {String((entry as any).content ?? (entry as any).answer ?? (entry as any).summary ?? '').slice(0, 120)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </>
                    )}

                    {(detail.refineIterations || []).length > 0 && (
                      <>
                        <h5>{t('review.optimizer.refine', 'Instruction refinement')}</h5>
                        {(detail.refineIterations || []).map((it) => (
                          <div key={it.iteration} className="opt-refine-iter">
                            <div>
                              #{it.iteration} {it.improved
                                ? t('review.optimizer.refineKept', 'kept — grades improved')
                                : t('review.optimizer.refineReverted', 'reverted — no improvement')}{' '}
                              <span className="opt-eval-badges">
                                <span className="opt-grade-good">✓{it.good}</span>
                                <span className="opt-grade-partial">△{it.partial}</span>
                                <span className="opt-grade-poor">✗{it.poor}</span>
                              </span>
                            </div>
                            {it.improved && <div className="opt-addendum detail-muted">{it.addendum}</div>}
                          </div>
                        ))}
                      </>
                    )}

                    {detail.metrics && (
                      <p className="detail-muted">
                        {t('review.optimizer.metrics', 'LLM calls')}: {detail.metrics.llmCalls ?? 0} ·{' '}
                        {t('review.optimizer.tokens', 'tokens')}: {detail.metrics.estTokens ?? 0}
                        {' '}(≈${(((detail.metrics.estTokens ?? 0) / 1_000_000) * APPROX_USD_PER_MTOKEN).toFixed(3)}) ·{' '}
                        {Math.round((detail.metrics.wallTimeMs ?? 0) / 100) / 10}s ·{' '}
                        {t('review.optimizer.budget', 'budget')}: {Math.round(((detail.metrics.estTokens ?? 0) / (detail.metrics.tokenCap || 2_000_000)) * 1000) / 10}%
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default OptimizerRunsSection;
